import {
  badgeClasses,
  buttonBaseClasses,
  buttonClasses,
  checkboxClasses,
  styled,
  svgIconClasses,
  tabClasses,
  tableCellClasses,
  tableRowClasses,
  Theme,
  type ThemeOptions,
} from "@mui/material";

import { gridClasses } from "@mui/x-data-grid-pro";

//https://mui.com/x/react-data-grid/getting-started/#typescript
import type {} from "@mui/x-data-grid-pro/themeAugmentation";

import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import React from "react";

import AccentDanger from "../../icons/AccentDanger.svg?react";
import AccentInfo from "../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../icons/AccentSuccess.svg?react";
import AccentWarning from "../../icons/AccentWarning.svg?react";

import typography, { HEADER_FONT_SIZE } from "./sbTypography";
import zIndex from "./sbZIndex";

const buttonTextPrimaryStyle = ({ theme }: { theme: Theme }) => ({
  color: theme.palette.primary60.main,
  backgroundColor: "transparent",
  "&:hover": {
    background: "transparent",
    textShadow: theme.mixins.textShadowPrimary20,
    color: theme.palette.primary70.main,
  },
  "&:focus-visible": {
    borderWidth: "2px",
    borderColor: theme.palette.primary70.main,
    borderStyle: "solid",
    color: theme.palette.primary70.main,
  },
  "&:active": {
    color: theme.palette.primary80.main,
    textShadow: "none",
  },
  "&:disabled": {
    color: theme.palette.primary20.main,
  },
});

const AlertAccentInfo = styled(AccentInfo)(({ theme }) => ({
  color: theme.palette.text10.main,
  fill: theme.palette.accentInfo30.main,
}));

const AlertAccentWarning = styled(AccentWarning)(({ theme }) => ({
  color: theme.palette.text10.main,
  fill: theme.palette.accentWarning30.main,
}));

const AlertAccentDanger = styled(AccentDanger)(({ theme }) => ({
  color: theme.palette.text10.main,
  fill: theme.palette.accentDanger30.main,
}));

const AlertAccentSuccess = styled(AccentSuccess)(({ theme }) => ({
  color: theme.palette.text10.main,
  fill: theme.palette.accentSuccess30.main,
}));

const sbComponents: ThemeOptions["components"] = {
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiSvgIcon: {
    variants: [
      {
        props: { fontSize: "large" },
        style: { fontSize: "32px" },
      },
    ],
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        ...(typography && typography["body.small.regular"]),
        maxWidth: "90vw",
        backgroundColor: theme.palette.surfaceLight10.main,
        color: theme.palette.text50.main,
        boxShadow: theme.mixins.shadowSurfaceLight10,
      }),
      popper: ({ theme }) => ({
        zIndex: theme.zIndex.tooltip,
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.surfaceLight30.main,
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: ({ theme }) => ({
        background: theme.palette.overlay.main,
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundImage: "none",
        backgroundColor: theme.palette.surfaceLight20.main,
      }),
    },
  },
  MuiInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        borderRadius: 8,
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {
      style: { fontSize: "14px" },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: "14px",
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ ownerState, theme }) => ({
        ...(ownerState?.anchor === "left" && {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }),
        ...(ownerState?.anchor === "right" && {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }),
        ...(ownerState?.anchor === "top" && {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }),
        ...(ownerState?.anchor === "bottom" && {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }),
        backgroundImage: "none",
        backgroundColor: theme.palette.surfaceLight10.main,
      }),
    },
  },

  MuiTable: {
    styleOverrides: {
      root: {
        "& .MuiTableCell-head": {
          fontWeight: "bold",
          fontSize: 14,
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        [`& .${tableRowClasses.root}`]: {
          "&:last-child": {
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
            },
          },
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:last-child .MuiTableCell-body:last-child": {
          borderBottomRightRadius: 12,
        },
        "&:last-child .MuiTableCell-body:first-child": {
          borderBottomLeftRadius: 12,
        },
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        [`& .${tableRowClasses.root}`]: {
          [`& .${tableCellClasses.root}`]: {
            "&:first-child": {
              borderRadius: "12px 0 0 0",
            },
            "&:last-child": {
              borderRadius: "0 12px 0 0",
            },
          },
        },
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        boxShadow: "none",
        border: "1px solid rgb(0, 0, 0, 0.12)",
        overflow: "initial",
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: "12px",
      },
    },
  },
  MuiBreadcrumbs: {
    defaultProps: {
      separator: React.createElement(CaretRightIcon),
    },
    styleOverrides: {
      root: ({ theme }) => ({
        a: {
          textDecoration: "none",
          color: theme.palette.text40.main,
        },
      }),
    },
  },
  MuiAlert: {
    variants: [
      {
        props: { severity: "error" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentDanger10.main,
          boxShadow: theme.mixins.shadowAccentDanger20,
        }),
      },
      {
        props: { severity: "info" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentInfo10.main,
          boxShadow: theme.mixins.shadowAccentInfo20,
        }),
      },
      {
        props: { severity: "warning" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentWarning10.main,
          boxShadow: theme.mixins.shadowAccentWarning20,
          "& .MuiAlert-icon": {
            "> svg": {
              width: "24px",
              height: "24px",
            },
          },
        }),
      },
      {
        props: { severity: "error" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentDanger10.main,
          boxShadow: theme.mixins.shadowAccentDanger20,
        }),
      },
      {
        props: { severity: "success" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentSuccess10.main,
          boxShadow: theme.mixins.shadowAccentSuccess20,
        }),
      },
    ],
    defaultProps: {
      iconMapping: {
        info: React.createElement(AlertAccentInfo),
        warning: React.createElement(AlertAccentWarning),
        error: React.createElement(AlertAccentDanger),
        success: React.createElement(AlertAccentSuccess),
      },
    },
    styleOverrides: {
      root: {
        borderRadius: "4px",
        marginBottom: "16px",
        width: "fit-content",
        padding: "8px 24px 8px 24px",
        margin: 0,
        ...(typography && typography["body.medium.regular"]),
      },
      icon: {
        alignItems: "center",
      },
    },
  },
  MuiButton: {
    defaultProps: {
      variant: "contained",
    },
    variants: [
      {
        props: { size: "xSmall" },
        style: {
          padding: "6px 8px 6px 8px",
          gap: "8px",
          borderRadius: "4px",
          height: "24px",
          ...(typography && {
            ...typography["button.xSmall"],
          }),
          [`& .${svgIconClasses.root}`]: {
            width: "12px",
            height: "12px",
          },
        },
      },
      {
        props: { size: "small" },
        style: {
          padding: "8px 12px 8px 12px",
          gap: "8px",
          borderRadius: "4px",
          height: "32px",
          ...(typography && {
            ...typography["button.small"],
          }),
          [`& .${svgIconClasses.root}`]: {
            width: "16px",
            height: "16px",
          },
        },
      },

      {
        props: { size: "medium" },
        style: {
          padding: "10px 16px 10px 16px",
          gap: "8px",
          borderRadius: "6px",
          height: "40px",
          ...(typography && {
            ...typography["button.medium"],
          }),

          [`& .${svgIconClasses.root}`]: {
            width: "16px",
            height: "16px",
          },
        },
      },
      {
        props: { size: "large" },
        style: {
          padding: "12px 16px 12px 16px",
          gap: "8px",
          borderRadius: "8px",
          height: "48px",
          ...(typography && {
            ...typography["button.large"],
          }),
          [`& .${svgIconClasses.root}`]: {
            width: "24px",
            height: "24px",
          },
        },
      },

      {
        props: { size: "xLarge" },
        style: {
          padding: "12px 16px 12px 16px",
          gap: "8px",
          borderRadius: "8px",
          height: "56px",
          ...(typography && {
            ...typography["button.xLarge"],
          }),
          [`& .${svgIconClasses.root}`]: {
            width: "32px",
            height: "32px",
          },
        },
      },
      {
        props: { variant: "text", color: "primary" },
        style: buttonTextPrimaryStyle,
      },
      {
        props: { variant: "outlined", color: "primary" },
        style: ({ theme }) => ({
          color: theme.palette.primary50.main,
          borderWidth: "2px",
          borderColor: theme.palette.primary50.main,
          backgroundColor: "transparent",
          "&:hover": {
            color: theme.palette.primary50.main,
            borderColor: theme.palette.primary50.main,
            borderWidth: "2px",
            boxShadow: theme.mixins.shadowPrimary20,
          },
          "&:focus-visible": {
            color: theme.palette.primary70.main,
            borderColor: theme.palette.primary70.main,
            borderWidth: "2px",
            backgroundColor: theme.palette.primary10.main,
            boxShadow: theme.mixins.shadowPrimary10,
          },
          "&:active": {
            color: theme.palette.primary80.main,
            borderColor: theme.palette.primary80.main,
            borderWidth: "2px",
            boxShadow: theme.mixins.shadowPrimary10,
          },
          "&:disabled": {
            color: theme.palette.primary20.main,
            borderColor: theme.palette.primary20.main,
            borderWidth: "2px",
          },
        }),
      },
      {
        props: { variant: "contained", color: "primary" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.primary50.main,
          color: theme.palette.text10.main,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: theme.palette.primary40.main,
            boxShadow: theme.mixins.shadowPrimary20,
          },
          "&:focus-visible": {
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: theme.palette.primary70.main,
            boxShadow: theme.mixins.shadowPrimary10,
          },
          "&:active": {
            borderWidth: "0px",
            backgroundColor: theme.palette.primary60.main,
            boxShadow: theme.mixins.shadowPrimary10,
          },
          "&:disabled": {
            backgroundColor: theme.palette.primary10.main,
            color: theme.palette.text10.main,
          },
        }),
      },
      {
        props: { variant: "text", color: "secondary" },
        style: ({ theme }) => ({
          color: theme.palette.secondary60.main,
          backgroundColor: "transparent",
          "&:hover": {
            background: "transparent",
            textShadow: theme.mixins.textShadowSecondary20,
            color: theme.palette.secondary50.main,
          },
          "&:focus-visible": {
            borderWidth: "2px",
            borderColor: theme.palette.secondary60.main,
            borderStyle: "solid",
          },
          "&:active": {
            color: theme.palette.secondary70.main,
            textShadow: "none",
          },
          "&:disabled": {
            color: theme.palette.secondary20.main,
          },
        }),
      },
      {
        props: { variant: "outlined", color: "secondary" },
        style: ({ theme }) => ({
          color: theme.palette.secondary60.main,
          borderWidth: "2px",
          borderColor: theme.palette.secondary60.main,
          backgroundColor: "transparent",
          "&:hover": {
            color: theme.palette.secondary60.main,
            borderColor: theme.palette.secondary60.main,
            borderWidth: "2px",
            boxShadow: theme.mixins.shadowSecondary20,
          },
          "&:focus-visible": {
            color: theme.palette.secondary70.main,
            borderColor: theme.palette.secondary70.main,
            borderWidth: "2px",
            backgroundColor: theme.palette.secondary10.main,
          },
          "&:active": {
            color: theme.palette.secondary80.main,
            borderColor: theme.palette.secondary80.main,
            borderWidth: "2px",
            boxShadow: theme.mixins.shadowSecondary10,
          },
          "&:disabled": {
            color: theme.palette.secondary20.main,
            borderColor: theme.palette.secondary20.main,
            borderWidth: "2px",
          },
        }),
      },
      {
        props: { variant: "contained", color: "secondary" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.secondary60.main,
          color: theme.palette.text10.main,
          "&:hover": {
            backgroundColor: theme.palette.secondary50.main,
            boxShadow: theme.mixins.shadowSecondary20,
          },
          "&:focus-visible": {
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: theme.palette.secondary80.main,
            boxShadow: theme.mixins.shadowSecondary10,
          },
          "&:active": {
            borderWidth: "0px",
            backgroundColor: theme.palette.secondary70.main,
            boxShadow: theme.mixins.shadowSecondary10,
          },
          "&:disabled": {
            backgroundColor: theme.palette.secondary10.main,
            color: theme.palette.text10.main,
          },
        }),
      },
      {
        props: { variant: "contained", color: "error" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentDanger30.main,
          color: theme.palette.text10.main,
          "&:hover": {
            backgroundColor: theme.palette.accentDanger50.main,
          },
          "&:focus-visible": {
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: theme.palette.accentDanger50.main,
          },
          "&:active": {
            borderWidth: "0px",
            backgroundColor: theme.palette.accentDanger30.main,
            boxShadow: theme.mixins.shadowSecondary10,
          },
          "&:disabled": {
            backgroundColor: theme.palette.accentDanger10.main,
            color: theme.palette.text10.main,
          },
        }),
      },
    ],

    styleOverrides: {
      root: () => ({
        textTransform: "none",
        boxSizing: "border-box",
        [`& .${buttonClasses.icon}`]: {
          marginLeft: "0px",
          marginRight: "0px",
        },
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        marginRight: "10px",
      },
      firstButton: {
        boxShadow: "none",
      },
      middleButton: {
        boxShadow: "none",
        borderLeft: "0",
        marginLeft: "2px",
      },
      lastButton: {
        borderLeft: "0",
        boxShadow: "none",
      },
    },
    defaultProps: {
      color: "primary",
    },
  },
  MuiToggleButton: {
    variants: [
      {
        props: { color: "primary" },
        style: ({ theme }) => ({
          color: theme.palette.text40.main,
          backgroundColor: theme.palette.primary10.main,
          "&.Mui-selected": {
            color: theme.palette.text50.main,
            borderWidth: "4px",
            backgroundColor: theme.palette.primary30.main,
            [`&.${buttonBaseClasses.root}`]: {
              borderRadius: "8px",
            },
            zIndex: zIndex?.selectedToggleButton,
            "&:hover": {
              backgroundColor: theme.palette.primary40.main,
            },
          },
          "&:hover": {
            backgroundColor: theme.palette.primary20.main,
          },
          boxShadow: theme.mixins.shadowSecondary10,
          ...(typography && {
            ...typography["button.medium"],
          }),
        }),
      },
      {
        props: { size: "xSmall" },
        style: {
          ...(typography && {
            ...typography["button.xSmall"],
          }),
        },
      },
      {
        props: { color: "secondary" },
        style: ({ theme }) => ({
          color: theme.palette.secondary60.main,
          backgroundColor: theme.palette.secondary10.main,
          "&.Mui-selected": {
            color: theme.palette.text10.main,
            borderWidth: "4px",
            backgroundColor: theme.palette.secondary50.main,
            [`&.${buttonBaseClasses.root}`]: {
              borderRadius: "8px",
            },
            zIndex: zIndex?.selectedToggleButton,
            "&:hover": {
              backgroundColor: theme.palette.secondary40.main,
            },
          },
          "&:hover": {
            backgroundColor: theme.palette.secondary20.main,
          },
          boxShadow: theme.mixins.shadowSecondary10,
        }),
      },
    ],
    styleOverrides: {
      root: {
        height: "fit-content",
        padding: "12px",
        textTransform: "none",
        borderRadius: "8px",
        border: "0",
      },
    },
  },

  MuiChip: {
    variants: [
      {
        props: { variant: "primary" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.primary70.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "secondary" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.secondary70.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "info" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentInfo30.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "success" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentSuccess30.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "error" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentDanger30.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "errorLight" },
        style: ({ theme }) => ({
          backgroundColor: theme.palette.accentDanger20.main,
          color: theme.palette.text10.main,
        }),
      },
      {
        props: { variant: "outline" },
        style: ({ theme }) => ({
          color: theme.palette.text40.main,
          borderColor: theme.palette.text40.main,
          borderWidth: "1.5px",
          borderStyle: "solid",
        }),
      },
    ],
    styleOverrides: {
      root: {
        padding: "3px 16px 5px 16px",
        alignItems: "center",
        gap: "16px",
        borderRadius: "35px",
        width: "fit-content",
        background: "none",
        ...(typography && typography["button.medium"]),
      },
      label: {
        padding: "0px",
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: "8px 20px",
        color: theme.palette.text40.main,
        padding: 0,
        minHeight: "fit-content",
        textTransform: "none",
        ...(typography && typography["button.medium"]),
        [`&.${tabClasses.selected}`]: {
          color: theme.palette.text50.main,
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: "fit-content",
      },
    },
  },
  MuiTextField: {
    variants: [
      {
        props: { size: "small" },
        style: {
          "& .MuiInputBase-input": {
            ...(typography && typography["body.xSmall.regular"]),
            padding: 0,
          },
          padding: 0,
          "& .MuiSelect-select": {
            ...(typography && typography["body.xSmall.regular"]),
            padding: 0,
          },

          "& .MuiInputBase-root": {
            padding: "8px",
          },
        },
      },
      {
        props: { size: "medium" },
        style: {
          "& .MuiInputBase-input": {
            ...(typography && typography["body.small.regular"]),
            padding: 0,
          },
          "& .MuiSelect-select": {
            ...(typography && typography["body.small.regular"]),
            padding: 0,
          },

          "& .MuiInputBase-root": {
            padding: "12px",
          },
        },
      },
      {
        props: { size: "large" },
        style: {
          "& .MuiInputBase-input": {
            ...(typography && typography["body.medium.regular"]),
            padding: 0,
          },
          "& .MuiSelect-select": {
            ...(typography && typography["body.medium.regular"]),
            padding: 0,
          },

          "& .MuiInputBase-root": {
            padding: "12px",
          },
        },
      },
      {
        props: { color: "error" },
        style: ({ theme }) => ({
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.accentDanger30.main,
          },
        }),
      },
      {
        props: { color: "success" },
        style: ({ theme }) => ({
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.accentSuccess30.main,
          },
        }),
      },
    ],
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiInputBase-root": {
          borderRadius: "4px !important",
          borderWidth: "1px",
          borderColor: theme.palette.text30.main,
          color: theme.palette.text40.main,
        },
        "&:hover": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary40.main,
          },
          "& .MuiInputBase-root": {
            boxShadow: theme.mixins.shadowSurfaceLight10,
            ...theme.applyStyles("dark", {
              boxShadow: theme.mixins.shadowSurfaceDark10,
            }),
          },
        },
        "& .Mui-focused fieldset": {
          borderColor: theme.palette.primary60.main,
          boxShadow: theme.mixins.shadowPrimary10,
        },
        "&:disabled": {
          "& .MuiInputBase-root": {
            borderColor: theme.palette.text20.main,
            color: theme.palette.text20.main,
          },
        },
        "&.seamless": {
          "& .MuiInputBase-input": {
            fontSize: HEADER_FONT_SIZE.six,
            color: theme.palette.text50.main,
            padding: 0,
            height: "fit-content",
          },
          "&:hover": {
            "& .MuiInputBase-input": {
              boxShadow: theme.mixins.shadowPrimary10,
              color: theme.palette.text40.main,
            },
          },
          "& .MuiInputBase-root": {
            padding: 0,
            "& .MuiOutlinedInput-notchedOutline": {
              display: "none",
            },
          },
        },
      }),
    },
  },
  MuiCheckbox: {
    variants: [
      {
        props: { color: "secondary" },
        style: ({ theme }) => ({
          [`&.${checkboxClasses.checked}`]: {
            color: theme.palette.secondary50.main,
          },
        }),
      },
      {
        props: { color: "primary" },
        style: ({ theme }) => ({
          [`&.${checkboxClasses.checked}`]: {
            color: theme.palette.primary50.main,
          },
        }),
      },
    ],
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "0px",
        [`&.${checkboxClasses.checked}`]: {
          color: theme.palette.primary50.main,
        },
        "& .MuiSvgIcon-root": {
          width: "24px",
          height: "24px",
        },
      }),
    },
  },
  MuiDataGrid: {
    defaultProps: {
      hideFooterPagination: true,
      slotProps: {
        filterPanel: {
          sx: (theme) => ({
            backgroundColor: theme.palette.surfaceLight10.main,
            border: `1px solid ${theme.palette.surfaceLight30.main}`,
            borderRadius: "4px",
            "& .MuiButton-contained": {
              ...buttonTextPrimaryStyle({ theme }),
              "&:hover": {
                background: "transparent",
                textShadow: theme.mixins.textShadowPrimary20,
                color: theme.palette.primary70.main,
                boxShadow: "None",
              },
            },
          }),
          filterFormProps: {
            columnInputProps: {
              ["data-testid"]: "select-filter-column",
            },
            // @ts-ignore
            "data-testid": "filter-form",
          },
        },
        columnsManagement: {
          disableResetButton: true,
        },
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderStyle: "none !important",
        [`& .${gridClasses.columnHeader}`]: {
          backgroundColor: theme.palette.primary10.main,
        },
        [`& .${gridClasses.columnHeaderTitle}`]: {
          backgroundColor: theme.palette.primary10.main,
          ...(typography && typography["body.small.semibold"]),
        },

        [`&. ${gridClasses.main}`]: {
          border: `1px solid ${theme.palette.surfaceLight40}`,
          boxShadow: `0px 2px 8px 0px ${theme.palette.surfaceLight10}`,
        },
        [`& .${gridClasses.cell}`]: {
          padding: "0 12px",
          ...(typography && typography["body.small.regular"]),
          lineHeight: undefined, // use the line height from the data grid, not the typography.
        },
        [`& .${badgeClasses.badge}`]: {
          color: theme.palette.text10.main,
        },
        "& .Mui-even": {
          backgroundColor: theme.palette.surfaceLight10.main,
        },
        "& .Mui-odd": {
          backgroundColor: theme.palette.surfaceLight20.main,
        },
        [`& .${gridClasses.row}`]: {
          "&:hover": {
            backgroundColor: theme.palette.surfaceLight40.main,
          },
        },
        [`& .${gridClasses.overlay}`]: {
          fontSize: "12px",
        },
        [`& .${gridClasses.columnHeaders} .${gridClasses.scrollbarFiller}`]: {
          backgroundColor: theme.palette.primary10.main,
        },
      }),
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text50.main,
      }),
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text50.main,
      }),
    },
  },
};

declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    xSmall: true;
    xLarge: true;
  }
}

declare module "@mui/material/ToggleButton" {
  interface ToggleButtonPropsSizeOverrides {
    xSmall: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    primary: true;
    secondary: true;
    info: true;
    success: true;
    successLight: true;
    outline: true;
    error: true;
    errorLight: true;
  }
}

declare module "@mui/material/TextField" {
  interface TextFieldPropsSizeOverrides {
    large: true;
  }
}
export interface CustomColumnsPanelProps {
  resetColumnVisibilityModel: () => void;
}

// augment the props for `columnsPanel` slot
declare module "@mui/x-data-grid" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface ColumnsPanelPropsOverrides extends CustomColumnsPanelProps {}
}

export default sbComponents;
